import { useEffect, useMemo, useRef } from "react";

/**
 * PDF open parameters appended to the viewer URL. `navpanes=0` hides the
 * thumbnail/bookmark sidebar so only the scrolling pages are shown.
 */
const VIEWER_PARAMS = "#toolbar=1&navpanes=0&statusbar=0&view=FitH";

/**
 * Renders a base64-encoded PDF in an iframe using an object URL (revoked on
 * unmount / change). Object URLs avoid the size limits and rendering quirks of
 * very large `data:` URLs.
 */
export function PdfPreview({
  base64,
  title,
  className,
}: {
  base64: string;
  title?: string;
  className?: string;
}) {
  const urlRef = useRef<string | null>(null);

  const url = useMemo(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    const byteChars = atob(base64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      bytes[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: "application/pdf" });
    const objectUrl = URL.createObjectURL(blob);
    urlRef.current = objectUrl;
    return objectUrl;
  }, [base64]);

  useEffect(() => {
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, []);

  return (
    <iframe
      src={`${url}${VIEWER_PARAMS}`}
      title={title ?? "PDF preview"}
      className={className ?? "h-full w-full"}
    />
  );
}
